import { useState, useCallback } from "react";
import { RequirePermission } from "@/components/RequirePermission";
import {
  useApprovalRequests,
  useApprovalPendingCount,
  useApprovalRealtimeToasts } from
"@/hooks/useApprovalSystem";
import { usePagination } from "@/hooks/usePagination";
import { PaginationControls } from "@/components/controller/PaginationControls";
import { ApprovalStatsRow } from "@/components/controller/approvals/ApprovalStatsRow";
import { ApprovalListRow } from "@/components/controller/approvals/ApprovalListRow";
import { ApprovalDetailPanel } from "@/components/controller/approvals/ApprovalDetailPanel";
import { ApprovalHistoryPanel } from "@/components/controller/approvals/ApprovalHistoryPanel";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Search, ArrowLeft, FileSpreadsheet, FileText, Printer } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { PageActions } from "@/components/shared/PageActions";
import { useExportActions } from "@/lib/export";

const TYPE_FILTER_OPTIONS = [
{ value: "all", label: "All Types" },
{ value: "rate_change", label: "Rate Change" },
{ value: "low_margin", label: "Low Margin" },
{ value: "change_order", label: "Change Order" },
{ value: "retainage_release", label: "Retainage Release" },
{ value: "void_record", label: "Void" },
{ value: "bill_approve", label: "Bill Approval" },
{ value: "invoice_approve", label: "Bill Approval (legacy)" },
{ value: "draw_approve", label: "Draw" },
{ value: "vendor_approve", label: "Vendor Approval" },
{ value: "reopen_wo", label: "Reopen WO" },
{ value: "backcharge", label: "Backcharge" }];


const SORT_OPTIONS = [
{ value: "newest", label: "Newest First" },
{ value: "oldest", label: "Oldest First" },
{ value: "urgency", label: "Urgency" }];

const ApprovalsPageActions = ({ data, tab, typeFilter }: { data: any[]; tab: string; typeFilter: string }) => {
  const { doExport } = useExportActions({ entityType: "approvals" });
  const hasData = data.length > 0;
  return (
    <PageActions actions={[
      {
        label: "Export to Excel",
        icon: FileSpreadsheet,
        onClick: () => hasData && doExport("excel", data, `tab: ${tab}, type: ${typeFilter}`),
        permission: hasData,
      },
      {
        label: "Export to CSV",
        icon: FileText,
        onClick: () => hasData && doExport("csv", data, `tab: ${tab}, type: ${typeFilter}`),
        permission: hasData,
      },
      {
        label: "Print",
        icon: Printer,
        onClick: () => hasData && doExport("print", data, `tab: ${tab}, type: ${typeFilter}`),
        permission: hasData,
      },
    ]} />
  );
};


const Approvals = () => {
  const isMobile = useIsMobile();
  const [tab, setTab] = useState<"pending" | "all" | "sent" | "completed">("pending");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [search, setSearch] = useState("");
  const [selectedLineIds, setSelectedLineIds] = useState<Set<string>>(new Set());
  const [totalLineCount, setTotalLineCount] = useState(0);

  const pagination = usePagination();
  const { data: pendingCount } = useApprovalPendingCount();
  const { data: requests, isLoading } = useApprovalRequests(tab, pagination);

  // Realtime toast notifications
  const handleRealtimeNotif = useCallback((payload: any) => {
    const typeLabels: Record<string, string> = {
      approved: "Request Approved",
      rejected: "Request Rejected",
      info_requested: "Information Requested",
      forwarded: "Request Forwarded",
      request_received: "New Approval Request"
    };
    toast(typeLabels[payload.notification_type] ?? "Approval Update", { duration: 5000 });
  }, []);
  useApprovalRealtimeToasts(handleRealtimeNotif);

  // Filter and sort
  let filtered = requests ?? [];
  if (typeFilter !== "all") {
    if (typeFilter === "bill_approve") {
      filtered = filtered.filter((r) => r.request_type === "bill_approve" || r.request_type === "invoice_approve");
    } else {
      filtered = filtered.filter((r) => r.request_type === typeFilter);
    }
  }
  if (search.trim()) {
    const q = search.toLowerCase();
    filtered = filtered.filter(
      (r) =>
      r.title.toLowerCase().includes(q) ||
      (r.projects?.name ?? "").toLowerCase().includes(q) ||
      r.request_type.includes(q)
    );
  }
  if (sortBy === "oldest") {
    filtered = [...filtered].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  } else if (sortBy === "urgency") {
    filtered = [...filtered].sort((a, b) => (a.urgency === "urgent" ? -1 : 1) - (b.urgency === "urgent" ? -1 : 1));
  }

  // Mobile: single-panel flow
  const mobileView = isMobile ? (selectedId ? "detail" : "list") : "desktop";

  return (
    <RequirePermission anyOf={["approve_bills", "approve_payments", "edit_contract_sov", "view_financials"]}>
      <div className="space-y-4">
        {/* Mobile back button when viewing detail */}
        {isMobile && selectedId && (
          <Button variant="ghost" size="sm" className="-ml-2" onClick={() => { setSelectedId(null); setSelectedLineIds(new Set()); setTotalLineCount(0); }}>
            <ArrowLeft className="mr-1 h-4 w-4" />Back to Queue
          </Button>
        )}

        {(mobileView === "list" || mobileView === "desktop") && (
          <>
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-bold tracking-tight">Approval Queue</h1>
              <ApprovalsPageActions data={filtered} tab={tab} typeFilter={typeFilter} />
            </div>

            <ApprovalStatsRow />

            {/* Tabs */}
            <Tabs value={tab} onValueChange={(v) => {setTab(v as any);setSelectedId(null);setSelectedLineIds(new Set());setTotalLineCount(0);pagination.setPage(1);}}>
              <TabsList>
                <TabsTrigger value="pending" className="gap-1.5">
                  Pending
                  {(pendingCount ?? 0) > 0 &&
                  <Badge variant="destructive" className="text-[10px] px-1.5 py-0 animate-pulse">
                      {pendingCount}
                    </Badge>
                  }
                </TabsTrigger>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="sent">Sent</TabsTrigger>
                <TabsTrigger value="completed">Done</TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Filter bar */}
            <div className="flex flex-wrap items-center gap-2">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[140px] md:w-[160px]">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  {TYPE_FILTER_OPTIONS.map((o) =>
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  )}
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[120px] md:w-[140px]">
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map((o) =>
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  )}
                </SelectContent>
              </Select>
              <div className="relative flex-1 min-w-[140px]">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search…"
                  className="pl-8"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)} />
              </div>
              <span className="text-xs text-muted-foreground">{filtered.length}</span>
            </div>
          </>
        )}

        {/* ── Mobile: Single-Panel Flow ── */}
        {isMobile ? (
          mobileView === "list" ? (
            <div className="space-y-1">
              {isLoading && <p className="p-4 text-muted-foreground text-sm">Loading…</p>}
              {!isLoading && filtered.length === 0 && <p className="p-4 text-muted-foreground text-sm">No requests found.</p>}
              {filtered.map((r) => (
                <ApprovalListRow
                  key={r.id}
                  request={r}
                  isSelected={false}
                  onClick={() => { setSelectedId(r.id); setSelectedLineIds(new Set()); setTotalLineCount(0); }}
                />
              ))}
              <PaginationControls pagination={pagination} />
            </div>
          ) : (
            <div className="space-y-4">
              <ApprovalDetailPanel
                requestId={selectedId!}
                selectedLineIds={selectedLineIds}
                onSelectedLineIdsChange={setSelectedLineIds}
                onTotalLineCount={setTotalLineCount}
              />
              <ApprovalHistoryPanel
                requestId={selectedId!}
                showActions={tab === "pending"}
                showWithdraw={tab === "sent"}
                onWithdraw={() => setSelectedId(null)}
                selectedLineIds={selectedLineIds}
                totalLineCount={totalLineCount}
              />
            </div>
          )
        ) : (
          /* ── Desktop: 3-Panel Layout ── */
          <div className="flex gap-3 min-h-[600px] max-h-[75vh]">
            {/* LEFT: Queue (25%) */}
            <div className="w-1/4 min-w-[220px] border rounded-lg overflow-auto flex flex-col bg-primary-foreground">
              {isLoading && <p className="p-4 text-muted-foreground text-sm">Loading…</p>}
              {!isLoading && filtered.length === 0 &&
              <p className="p-4 text-muted-foreground text-sm">No requests found.</p>
              }
              <div className="flex-1 overflow-auto">
                {filtered.map((r) =>
                <ApprovalListRow
                  key={r.id}
                  request={r}
                  isSelected={selectedId === r.id}
                  onClick={() => { setSelectedId(r.id); setSelectedLineIds(new Set()); setTotalLineCount(0); }} />
                )}
              </div>
              <div className="border-t">
                <PaginationControls pagination={pagination} />
              </div>
            </div>

            {/* CENTER: Detail (40%) */}
            <div className="flex-1 border rounded-lg overflow-auto">
              {selectedId ?
              <ApprovalDetailPanel
                requestId={selectedId}
                selectedLineIds={selectedLineIds}
                onSelectedLineIdsChange={setSelectedLineIds}
                onTotalLineCount={setTotalLineCount}
              /> :
              <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                  Select a request to view details
                </div>
              }
            </div>

            {/* RIGHT: Actions + History (35%) */}
            <div className="w-[320px] min-w-[280px] border rounded-lg overflow-auto">
              {selectedId ?
              <ApprovalHistoryPanel
                requestId={selectedId}
                showActions={tab === "pending"}
                showWithdraw={tab === "sent"}
                onWithdraw={() => setSelectedId(null)}
                selectedLineIds={selectedLineIds}
                totalLineCount={totalLineCount}
              /> :
              <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
                  Select a request
                </div>
              }
            </div>
          </div>
        )}
      </div>
    </RequirePermission>);

};

export default Approvals;